/* The cookie panel (snippets/st-consent.liquid says what it looks like and why).

   Built on Shopify's Customer Privacy API. Carried over from the Givino banner (Elms, 2026-09),
   where each of these was measured on a real storefront:
   - shouldShowBanner() stays true after a choice is recorded, so it is not the only gate: the
     recorded consent is. A visitor who has answered is never asked again on the next page.
   - setTrackingConsent() writes to Shopify's Storefront API, so it fails on 127.0.0.1 and works on a
     storefront domain. Verify on a theme preview URL, never on the local theme dev server.
   - Shopify's own banner must be switched off in the admin (Settings > Customer privacy > Cookie
     banner) or both show. Until it is, window.privacyBanner exists and this panel stays out of the
     way; the footer link then opens Shopify's own preferences instead.

   Nothing moves focus on page load. Focus moves only when the visitor acts: into the preferences on
   opening them, and back to where they started when the panel closes. */
(() => {
  const FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];
  const CATEGORIES = ['analytics', 'marketing', 'preferences'];
  const REOPEN = 'a[href="#shopifyReshowConsentBanner"], [data-st-consent-open]';

  class StConsent extends HTMLElement {
    connectedCallback() {
      this.toast = document.getElementById('st-ck-toast');
      this.opener = null;
      this.querySelector('[data-st-accept]')?.addEventListener('click', () => this.apply(true, true));
      this.querySelector('[data-st-decline]')?.addEventListener('click', () => this.apply(false, true));
      this.querySelector('[data-st-prefs]')?.addEventListener('click', () => this.view('prefs'));
      this.querySelector('[data-st-back]')?.addEventListener('click', () => this.view('main'));
      this.querySelector('[data-st-save]')?.addEventListener('click', () => this.save());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.dataset.view === 'prefs') this.view('main');
      });
      document.addEventListener('click', (event) => this.reopen(event));

      // Shopify's banner script loads asynchronously from the page head; by the load event it has
      // either defined window.privacyBanner or it is switched off.
      if (document.readyState === 'complete') this.start();
      else window.addEventListener('load', () => this.start(), { once: true });
    }

    /** Shopify's own banner is still switched on in the admin, so it stays in charge. */
    get shopifyOwns() {
      return Boolean(window.privacyBanner);
    }

    start() {
      if (this.shopifyOwns) return;
      this.api((privacy) => {
        if (privacy.shouldShowBanner() && !this.answered(privacy)) this.show(false);
      });
    }

    api(then) {
      if (this.privacy) return then(this.privacy);
      if (typeof window.Shopify?.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures(FEATURE, (error) => {
        if (error || !window.Shopify.customerPrivacy) return; // no API, no panel wired to nothing
        this.privacy = window.Shopify.customerPrivacy;
        then(this.privacy);
      });
    }

    answered(privacy) {
      let consent = {};
      try {
        consent = privacy.currentVisitorConsent() || {};
      } catch (err) {
        return false;
      }
      return CATEGORIES.concat('sale_of_data').some((key) => consent[key] === 'yes' || consent[key] === 'no');
    }

    /** The footer's "Cookie preferences" link, ours or the one Shopify adds. */
    reopen(event) {
      const link = event.target.closest?.(REOPEN);
      if (!link) return;
      event.preventDefault();
      if (this.shopifyOwns) {
        window.privacyBanner.showPreferences?.();
        return;
      }
      this.opener = link;
      this.api(() => this.show(true));
    }

    show(toPrefs) {
      this.hidden = false;
      this.classList.remove('is-in');
      requestAnimationFrame(() => this.classList.add('is-in'));
      this.view(toPrefs ? 'prefs' : 'main', !toPrefs);
    }

    view(name, quiet = false) {
      const prefs = name === 'prefs';
      this.dataset.view = name;
      this.querySelector('[data-st-main]').hidden = prefs;
      this.querySelector('[data-st-panel]').hidden = !prefs;
      if (prefs) this.reflect();
      if (quiet) return;
      (prefs ? this.querySelector('.st-ck__sw') : this.querySelector('[data-st-prefs]'))?.focus();
    }

    /** Show the visitor's own recorded answer, never a default. */
    reflect() {
      let consent = {};
      try {
        consent = this.privacy?.currentVisitorConsent() || {};
      } catch (err) {}
      CATEGORIES.forEach((key) => {
        const box = this.querySelector(`[data-category="${key}"]`);
        if (box) box.checked = consent[key] === 'yes';
      });
    }

    save() {
      const on = (key) => Boolean(this.querySelector(`[data-category="${key}"]`)?.checked);
      const choice = { analytics: on('analytics'), marketing: on('marketing'), preferences: on('preferences') };
      // sale_of_data is California's "sale or sharing"; for this shop that is ad targeting, so it
      // follows the marketing answer rather than adding a fourth switch nobody can interpret.
      choice.sale_of_data = choice.marketing;
      this.write(choice, choice.analytics || choice.marketing || choice.preferences);
    }

    apply(value) {
      this.write({ analytics: value, marketing: value, preferences: value, sale_of_data: value }, value);
    }

    write(choice, accepted) {
      const done = () => this.close(accepted);
      try {
        this.privacy.setTrackingConsent(choice, done);
      } catch (err) {
        done(); // never leave the panel stuck open on an API error
      }
    }

    close(accepted) {
      const hadFocus = this.contains(document.activeElement);
      this.hidden = true;
      this.classList.remove('is-in');
      this.say(accepted);
      // Focus goes back to where the visitor started: the footer link that opened the panel, or,
      // when it appeared on its own, the main content, rather than dropping to <body>.
      if (!hadFocus) return;
      const back = this.opener?.isConnected ? this.opener : document.getElementById('MainContent');
      if (back && !back.matches('a, button, [tabindex]')) back.setAttribute('tabindex', '-1');
      back?.focus({ preventScroll: true });
      this.opener = null;
    }

    say(accepted) {
      const toast = this.toast;
      if (!toast) return;
      const head = document.createElement('b');
      head.textContent = accepted ? toast.dataset.saved : toast.dataset.declined;
      const info = document.createElement('span');
      info.textContent = toast.dataset.info;
      toast.replaceChildren(head, ' ', info);
      toast.classList.add('is-on');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        toast.classList.remove('is-on');
        toast.replaceChildren();
      }, 6000);
    }
  }

  if (!customElements.get('st-consent')) customElements.define('st-consent', StConsent);
})();
